import * as React from 'react';
import TextareaAutosize, { type TextareaAutosizeProps } from 'react-textarea-autosize';

import { cn } from '@heidi/utils';

import { useIsExperimentalDesignSystemForms } from '../hooks/use-is-experimental-design-system-forms';
import {
  textareaResizeHandleClassesExperimental,
  textareaResizeMaskClassesExperimental,
  textareaVariantsExperimental,
} from './textarea.experimental.styles';
import { textareaResizableClasses, textareaVariants } from './textarea.styles';
import type { TextareaProps } from './textarea.types';

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      wrapperClassName,
      autoResize,
      minRows,
      maxRows,
      disabled,
      style,
      ...props
    },
    ref
  ) => {
    // Must stay above the `autoResize` early return — Rules of Hooks.
    const isExperimentalForms = useIsExperimentalDesignSystemForms();
    const resolvedVariants = isExperimentalForms
      ? textareaVariantsExperimental
      : textareaVariants;

    if (autoResize) {
      // `react-textarea-autosize` narrows `style.height` to `number` because
      // it owns the height computation. Our DS surface keeps the broader
      // React.CSSProperties contract for ergonomic parity with the native
      // textarea path, so we cast `style` here. Heights set via `style`
      // would be overridden by the autosize logic anyway; callers should
      // bound growth via `maxRows` / `minRows` instead.
      return (
        <TextareaAutosize
          {...props}
          ref={ref}
          disabled={disabled}
          minRows={minRows}
          maxRows={maxRows}
          style={style as TextareaAutosizeProps['style']}
          data-experimental={isExperimentalForms || undefined}
          className={cn(resolvedVariants(), 'resize-none', className)}
        />
      );
    }

    const textarea = (
      <textarea
        disabled={disabled}
        ref={ref}
        {...props}
        style={style}
        data-experimental={isExperimentalForms || undefined}
        className={cn(resolvedVariants(), textareaResizableClasses, className)}
      />
    );

    return (
      // `group` scopes the handle's hover/focus lift; emits no CSS itself.
      <div className={cn('group relative w-full', wrapperClassName)}>
        {textarea}
        {/* Masks Firefox's native resize handle (no ::-webkit-resizer equivalent there).
            pointer-events-none keeps drag-to-resize working through the mask. */}
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute bottom-px end-px size-4 rounded-ee-lg bg-surface transition-opacity',
            isExperimentalForms && textareaResizeMaskClassesExperimental,
            disabled && 'opacity-0'
          )}
        />
        <svg
          aria-hidden
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          className={cn(
            'pointer-events-none absolute bottom-1 end-1 text-text-tertiary transition-opacity rtl:-scale-x-100',
            isExperimentalForms && textareaResizeHandleClassesExperimental,
            disabled && 'opacity-0'
          )}
        >
          <path
            d="M9.25 3.25 3.25 9.25"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
          />
          <path
            d="M9.25 6.75 6.75 9.25"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
          />
        </svg>
      </div>
    );
  }
);
Textarea.displayName = 'Textarea';

export { Textarea };
